import { z } from 'zod';
/**
 * RFC 7807-shaped error envelope for every non-2xx the BFF emits.
 *
 * Deliberately narrow. `detail` is a human string; there is no `meta`/`extra`
 * bag, because an open-ended object on a public error path is how internals
 * leak. If a future route needs structured error data, add a named optional
 * field here and let the boot-time response-schema assert see it.
 */
export const ProblemDetailsSchema = z.object({
    type: z.string(),
    title: z.string(),
    status: z.number().int().min(400).max(599),
    detail: z.string().optional(),
    correlationId: z.string().optional(),
});
/**
 * Stable `type` URNs. These are part of the contract: US-Shop branches on them.
 */
export const PROBLEM_TYPES = {
    badRequest: 'urn:rheora:enrollment:bad-request',
    notFound: 'urn:rheora:enrollment:not-found',
    tenantNotResolved: 'urn:rheora:enrollment:tenant-not-resolved',
    sessionInvalid: 'urn:rheora:enrollment:session-invalid',
    idempotencyKeyRequired: 'urn:rheora:enrollment:idempotency-key-required',
    idempotencyKeyConflict: 'urn:rheora:enrollment:idempotency-key-conflict',
    captchaRequired: 'urn:rheora:enrollment:captcha-required',
    captchaInvalid: 'urn:rheora:enrollment:captcha-invalid',
    /**
     * ONE type for every rate-limit refusal (E-SF01 AC3).
     *
     * There is deliberately no `rate-limit-ip` / `rate-limit-session` pair. An
     * attacker who learns WHICH limiter tripped learns exactly which dimension to
     * rotate — mint a fresh `sid`, or move IP. The refusal must be
     * indistinguishable across limiters, which is why the discriminating
     * information does not exist in the contract at all rather than merely being
     * omitted by the handler. You cannot leak a field you never defined.
     */
    rateLimited: 'urn:rheora:enrollment:rate-limited',
    /**
     * 409 — the resolved meter already has an open or active enrollment.
     *
     * Quotation refuses a second quote on a locked ESIID (`US_ESIID_ALREADY_ACTIVE`
     * / `…/problems/esiid-locked`). That is a correct, expected BUSINESS condition,
     * not a fault: the customer either already enrolled with us, or someone else is
     * on that meter. Until QA-2026-08-05 the BFF flattened it to a bare 500, so the
     * SPA showed "try again in a moment" forever with no route forward.
     *
     * ⚠️ The refusal carries NO identifiers. Quotation's body names the ESIID and an
     * `openQuoteId`; neither crosses this boundary — the ESIID is non-enumerable by
     * design (E-SF03 AC3) and the quoteId is a server-side handle the sign saga
     * redeems (E-SF04). The route forward is contact-based, not id-based.
     */
    esiidAlreadyEnrolled: 'urn:rheora:enrollment:esiid-already-enrolled',
    /**
     * 409 — the resolved meter is not in a state ERCOT will switch.
     *
     * Quotation refuses the quote with `US_ERCOT_ESIID_INACTIVE` (422) when GMO
     * reports the service point as anything other than ACTIVE — PENDING, INACTIVE,
     * SUSPENDED, a standing SWITCH_HOLD, or absent from the ERCOT registry
     * altogether. Like `esiidAlreadyEnrolled` this is a STATE the premise is in,
     * not a fault in the request and not an outage, so it must not arrive as a 500
     * that tells the shopper to "try again in a moment" — the state will not change
     * because they clicked twice.
     *
     * ⚠️ NO IDENTIFIERS, and no restatement of WHICH state. The ESIID is
     * non-enumerable by design (E-SF03 AC3), and naming the exact status turns the
     * funnel into an ERCOT registry oracle: a caller who can distinguish
     * "SWITCH_HOLD" from "not in the registry" can walk meters and read their
     * standing. The customer is told the meter cannot be switched right now; the
     * discriminating detail stays in the operator log.
     */
    esiidNotActive: 'urn:rheora:enrollment:esiid-not-active',
    /**
     * 422 — the ESI ID does not match the delivery utility for that address.
     *
     * ERCOT ESI IDs are fixed-width PER TDU (Oncor 17 digits, CenterPoint 22), so a
     * 17-digit ESI ID presented against CenterPoint cannot be a real service point.
     * Quotation refuses it with `ESIID_TDSP_LENGTH_MISMATCH` (422). Unlike the two
     * conditions above this is BAD INPUT with a genuine self-service remedy — the
     * shopper re-enters the ESI ID — so it is the one refusal in this family whose
     * route forward is "correct it and continue" rather than "contact us".
     *
     * ⚠️ The upstream `detail` names the TDU code and the digit counts. Neither
     * crosses this boundary: publishing the expected width per TDU hands an ESI ID
     * enumerator its input validator for free. The shopper is told the ID does not
     * match their utility, which is the part they can act on.
     */
    esiidTdspMismatch: 'urn:rheora:enrollment:esiid-tdsp-mismatch',
    /**
     * E-SF16 / 422 — the chosen tariff is not one of the plans this offer carries.
     *
     * The BFF filters the shelf to the offered tariffs (ADR-0010 D4.4), but that
     * filter is PRESENTATION: `/bff/plans` is an ungated public route the customer
     * can call directly, and the plan-detail route is public catalogue data. THE
     * control is Quotation's `select-plan`, which refuses an off-list tariff with a
     * typed 422 (`US_TARIFF_NOT_IN_OFFER`). This type is that refusal, translated.
     *
     * ⚠️ NO IDENTIFIERS. Quotation's body echoes the offered tariff ids (safe there —
     * it is answering the CRM). Here the caller already sees the offered plans on
     * their own shelf, so repeating them buys nothing and widens a public error body.
     */
    planNotInOffer: 'urn:rheora:enrollment:plan-not-in-offer',
    /**
     * E-SF16 / 409 — the plan's price has moved since the offer was composed.
     *
     * `select-plan` RE-PRICES LIVE and compares the result against the price the
     * agent offered; a genuine change is a 409 that must be re-confirmed
     * (`QUOTE_REPRICE_REQUIRED`). This is a real customer-facing decision, not an
     * outage, so it gets its own type and its own copy with a "continue at the
     * current price" action.
     *
     * ⚠️ THE CONFIRMATION TOKEN (`repricedSnapshotId`) IS NOT ON THE WIRE. Quotation
     * returns it so a caller can echo it back; the BFF stashes it on the funnel
     * session instead and honours `confirmPriceChange: true` on the retry. That
     * keeps `ProblemDetails` the narrow, closed shape it is deliberately declared as
     * (see the note at the top of this file) — an upstream identifier on a public
     * error path is exactly what that narrowness exists to prevent.
     */
    offerPriceChanged: 'urn:rheora:enrollment:offer-price-changed',
    /**
     * E-SF16 / 409 — the offer itself can no longer be acted on.
     *
     * TWO upstream conditions, ONE type, because they have ONE remedy: the offer has
     * passed its expiry, or the quote behind it has moved to a stage where repointing
     * is not legal (`US_OFFER_SELECTION_NOT_ALLOWED` — already signed, rescinded,
     * expired, or revoked-and-superseded). In both cases nothing the customer can do
     * on this page helps; they need the agent to send a fresh offer.
     *
     * ⚠️ It is deliberately NOT merged with `planNotInOffer`. That one is about the
     * PLAN and is fixed by choosing a different card on the shelf; this one is about
     * the OFFER and is not fixable in-session. Collapsing them would put a "pick
     * another plan" affordance in front of a customer for whom no plan will work.
     */
    offerNoLongerAvailable: 'urn:rheora:enrollment:offer-no-longer-available',
    /**
     * E-SF18 / 503 — the unsigned contract PDF could not be produced right now.
     *
     * Quotation's E40 preview refuses with TWO typed 503s — `US_AGREEMENT_PREVIEW_
     * TIMEOUT` (the render exceeded its 10s budget) and `US_AGREEMENT_PREVIEW_
     * DISABLED` (the rasteriser host is not wired). They collapse to ONE type here
     * on purpose: the customer's only action is identical in both cases, and
     * splitting them would publish which half of our rendering stack is down.
     *
     * It is a SEPARATE type from `internal` because the correct UI response differs.
     * A 500 tells the storefront a server fault occurred and there is nothing to do;
     * this says "we could not draw your contract this time, try again" — and for the
     * timeout branch a retry genuinely works, because the next attempt may hit E40's
     * warm per-snapshot cache. Nothing partial is ever streamed and nothing is
     * persisted, so a retry cannot compound.
     *
     * ⚠️ NO IDENTIFIERS. Quotation's `detail` on these branches names the quoteId;
     * the quoteId is a server-side handle that never egresses (E-SF04), so the BFF
     * reads only the upstream machine `code` for its own log and sends none of it.
     */
    agreementPreviewUnavailable: 'urn:rheora:enrollment:agreement-preview-unavailable',
    /**
     * E-SF18 / 409 — there is no DRAFT contract to preview for this enrollment.
     *
     * Reached when the funnel session holds no DRAFT quote for the
     * (candidate, plan, ESIID) fingerprint being previewed, or when Quotation
     * reports the quote is no longer a DRAFT (`QUOTE_NOT_DRAFT` — it is already
     * signed) or not yet priced/customer-captured.
     *
     * ONE type for all of those, because the remedy is the same and the shopper is
     * in the same place: go back a step and re-establish the quote. Distinguishing
     * "already signed" from "never created" on an anonymous edge would let a caller
     * probe the state of an enrollment from outside it.
     */
    agreementPreviewNotReady: 'urn:rheora:enrollment:agreement-preview-not-ready',
    /**
     * 409 — Pricing declined to publish this plan's §25.475 average price.
     *
     * The standalone-route twin of `PlanListEntry.priceWithheld`, for the plan the
     * SPA asks about on its own: a deep-linked `/plan/:id`, or any card the shelf
     * list could not answer for. Same condition, same reason enum, same copy.
     *
     * ── WHY 409 AND NOT 503 ────────────────────────────────────────────────
     * Because "try again" is FALSE here. Pricing's refusal is a property of the
     * plan's advertised terms, so the next request refuses identically — a 503
     * would put a retry button in front of a customer for whom retrying can never
     * work, which is the same dead end `esiidAlreadyEnrolled` was written to stop.
     * 409 says what is true: the resource exists, and its state precludes the
     * representation. Contrast `agreementPreviewUnavailable`, which IS a 503
     * precisely because there a retry genuinely succeeds.
     *
     * ⚠️ NO FIGURE, NO AMOUNT, NO TOKEN. Upstream names the matched marketing text
     * and the un-publishable arithmetic; none of it crosses. The entire reason this
     * refusal exists is that we cannot compute a defensible number, so nothing
     * derived from the attempt may be shown to a customer.
     */
    /**
     * E-SF05 / 400 — the identity number the customer typed is not a structurally
     * valid SSN or ITIN.
     *
     * Accounts refuses it before any bureau call (`IDENTITY_INVALID` /
     * `IDENTITY_KIND_MISMATCH`). Until `108afea` the BFF discarded that reason and
     * answered 500, so a fat-fingered digit reached the shopper as "internal server
     * error" — untrue, unactionable, and counted against the service's error budget.
     *
     * ⚠️ THE COPY MUST NOT SAY WHAT WAS WRONG WITH IT, and this is a stronger rule
     * than the ESIID one next door. "Invalid format" and "failed verification" are
     * different answers about a real person's identity, and a caller who can tell
     * them apart has an enumeration oracle against SSNs. The storefront therefore
     * says only "check the number you entered" and deliberately knows less than the
     * server does. The distinguishing code lives in the operator log and has no path
     * to the response body.
     *
     * ⚠️ Deliberately NOT `badRequest`. Both are 400s, but this one has a REMEDY the
     * shopper can act on in-session (correct the field), and the storefront needs to
     * tell them apart to put focus on it. That is the same reason
     * `esiidNotActive` and `esiidAlreadyEnrolled` are two types and not one status.
     *
     * ⚠️ ADDING THIS IS PURELY ADDITIVE. A cached SPA bundle that predates it does
     * not match the new URN, falls through its `default` branch to the existing
     * generic alert, and behaves exactly as it does today — the same reasoning
     * `credit.ts` records for `informationAccuracyUndertaking`.
     */
    identityInvalid: 'urn:rheora:enrollment:identity-invalid',
    /**
     * E-SF05 / 400 — the credit check needs an SSN or ITIN, and the request carried
     * only a driver licence.
     *
     * The identity form accepts an SSN/ITIN, a driver licence, or both, because both
     * documents identify the applicant and the licence is worth capturing on its
     * own. Only the SSN/ITIN can reach the bureau: Accounts' credit-decision
     * endpoint validates `identityKind: z.enum(['SSN','ITIN'])` on a `.strict()`
     * object, so a licence number has nowhere to go in that call.
     *
     * ⚠️ A DIFFERENT CONDITION FROM `identityInvalid` ABOVE, AND THE PAIR MUST NOT
     * BE COLLAPSED. That one means "you gave us a number and it is not readable";
     * this one means "you gave us no number of that kind at all". The remedies are
     * different sentences — correct the field versus fill in the field — and both
     * are 400s, so only the URN can separate them.
     *
     * ⚠️ Unlike `identityInvalid`, saying WHY is safe here. That type withholds its
     * reason because "malformed" and "failed verification" are different answers
     * about a real person and distinguishing them is an enumeration oracle. This
     * refusal reveals nothing about anybody: it is a statement about our own
     * upstream's capabilities, decided before any identity was looked at.
     *
     * ⚠️ PURELY ADDITIVE, on the same reasoning as `identityInvalid` — a cached SPA
     * bundle that predates it falls through to the generic alert unchanged.
     *
     * When Accounts learns to accept a licence, this refusal is the one thing that
     * has to change.
     */
    identityNumberRequired: 'urn:rheora:enrollment:identity-number-required',
    priceNotPublished: 'urn:rheora:enrollment:price-not-published',
    notImplemented: 'urn:rheora:enrollment:not-implemented',
    internal: 'urn:rheora:enrollment:internal',
};
//# sourceMappingURL=problem-details.js.map